export interface LegalSection {
  heading: string
  paragraphs: string[]
}

export interface LegalDocument {
  slug: string
  title: string
  navLabel: string
  intro: string
  sections: LegalSection[]
}

export const legalDocuments: LegalDocument[] = [
  {
    slug: 'privacy-policy',
    title: 'Privacy Policy',
    navLabel: 'Privacy Policy',
    intro:
      'This Privacy Policy explains how information submitted through this website is handled.',
    sections: [
      {
        heading: 'Information we collect',
        paragraphs: [
          'We collect only the information you choose to provide through our inquiry form, such as your name, email address, phone number, state, service of interest, preferred contact method and the details of your message.',
          'We may also collect standard technical information generated when you visit the website, such as browser type, device type and pages viewed.',
        ],
      },
      {
        heading: 'How we use information',
        paragraphs: [
          'Information submitted through this website is used to respond to your inquiry, to discuss the services you are interested in and to maintain records of our communications.',
          'We do not sell information submitted through this website.',
        ],
      },
      {
        heading: 'Confidentiality',
        paragraphs: [
          'We treat client and prospective client information as private. Information is shared internally only with those involved in responding to your inquiry, and externally only where you have asked us to coordinate with another professional or where disclosure is required by law.',
        ],
      },
      {
        heading: 'Sensitive information',
        paragraphs: [
          'Please do not submit confidential financial, medical, legal or other sensitive information through this website. A member of our team will contact you to arrange an appropriate way to discuss your circumstances.',
        ],
      },
      {
        heading: 'Retention and security',
        paragraphs: [
          'We retain inquiry information for as long as reasonably necessary to respond to your request and to maintain appropriate business records, and we take reasonable measures to protect it. No method of transmission over the internet can be guaranteed to be completely secure.',
        ],
      },
      {
        heading: 'Your choices',
        paragraphs: [
          'You may ask us to update or remove the information you submitted through this website by contacting us using the inquiry form.',
        ],
      },
    ],
  },
  {
    slug: 'terms-of-use',
    title: 'Terms of Use',
    navLabel: 'Terms of Use',
    intro: 'By accessing and using this website, you agree to the terms set out below.',
    sections: [
      {
        heading: 'Use of this website',
        paragraphs: [
          'This website is provided for general informational purposes about the fiduciary and administrative services we offer. You agree to use the website only for lawful purposes and not in a way that interferes with its operation or the use of the website by others.',
        ],
      },
      {
        heading: 'No professional relationship',
        paragraphs: [
          'Accessing this website, or submitting an inquiry through it, does not create a fiduciary, professional or contractual relationship. Any engagement begins only when it has been agreed in writing and the applicable authority or documentation is in place.',
        ],
      },
      {
        heading: 'Accuracy of content',
        paragraphs: [
          'We make reasonable efforts to keep the content of this website accurate and current, but the content is provided without warranty of any kind. Services and responsibilities described here may vary depending on the circumstances, governing documents and applicable state law.',
        ],
      },
      {
        heading: 'Intellectual property',
        paragraphs: [
          'The content, layout and materials on this website are the property of the company and may not be copied, reproduced or redistributed without permission, except for ordinary personal reference.',
        ],
      },
      {
        heading: 'Third-party links',
        paragraphs: [
          'This website may reference or link to third-party resources. We are not responsible for the content, availability or practices of any third-party website.',
        ],
      },
      {
        heading: 'Changes to these terms',
        paragraphs: [
          'We may update these Terms of Use from time to time. Continued use of the website after any update constitutes acceptance of the revised terms.',
        ],
      },
    ],
  },
  {
    slug: 'disclaimer',
    title: 'Disclaimer',
    navLabel: 'Disclaimer',
    intro:
      'How to read the information on this website, and how our services are defined in each engagement.',
    sections: [
      {
        heading: 'Purpose of this website',
        paragraphs: [
          'This website is intended to give a clear overview of the fiduciary and administrative services we provide. It is general information rather than advice tailored to an individual situation, since fiduciary responsibilities and the services that apply depend on the circumstances, the governing documents and applicable state law.',
          'The right starting point is a conversation. Once we understand a matter, we can explain precisely which services apply and how they would be carried out.',
        ],
      },
      {
        heading: 'Our professional role',
        paragraphs: [
          'Our work is fiduciary and administrative. We provide the operational structure and oversight required to manage both financial and healthcare logistics seamlessly: administering estate assets and coordinating directly with banking institutions; managing distributions, paying expenses and navigating health insurance claims, benefits and medical billing; and organizing daily logistics while communicating transparently with families, beneficiaries and professional advisors.',
          'While we handle the financial, insurance and administrative sides of care, clinical medical opinions remain strictly with qualified healthcare professionals. We work right alongside these medical experts, ensuring they have the accurate information they need to provide the best care possible.',
          'The same principle applies to legal and tax matters: attorneys and accountants provide the opinions in their fields, and we administer, document and coordinate the work that follows from them.',
        ],
      },
      {
        heading: 'Scope of each engagement',
        paragraphs: [
          'Services are performed in accordance with the authority established in each matter, the terms of any governing documents and applicable law. The scope of our responsibilities is agreed and documented at the outset, so every party knows what we are handling.',
        ],
      },
      {
        heading: 'Individual circumstances',
        paragraphs: [
          'Each trust, estate and personal matter is different, and outcomes such as timelines, distributions and program eligibility are determined by the governing documents, the parties involved and applicable law rather than by our administration alone. What we commit to is careful, diligent and well-documented work within our authority.',
        ],
      },
      {
        heading: 'Third-party references',
        paragraphs: [
          'Where we reference or coordinate with outside resources and professionals, they remain responsible for their own services. We select and work with them carefully and keep the matter organized across everyone involved.',
        ],
      },
    ],
  },
]

export function getLegalDocument(slug: string): LegalDocument | undefined {
  return legalDocuments.find((document) => document.slug === slug)
}
